import { values } from "./values.ts";

export const REGION_LOCAL_STORAGE_KEY = "region";
export const REGION_URL_SEARCH_PARAM = "region";

const ALL_REGIONS = ["na", "eu"] as const;
export const DEFAULT_REGION: Region = "na";
export type Region = (typeof ALL_REGIONS)[number];
export type Environment = "msw" | "local" | "development" | "staging" | "production";
export type RuntimeConfig = BaseConfig & RegionSpecificConfig;

export interface RegionSpecificConfig {
    insights: {
        api: string;
        consentInitiatePage: string;
        login: string;
        logout: string;
    };
    migrate_web: {
        api: string;
        login: string;
    };
    reviews: {
        login: string;
    };
    sharedReports: {
        login: string;
    };
    app_registrations: {
        insights: string;
        remediation: string;
        migrate: string;
        archive: string;
    };
    google: {
        app_id: string;
        oauth_client_id: string;
    };
    logrocket?: {
        app_id?: string;
    };
    honeycomb?: {
        proxy: string;
    };
    // NA only: Mixpanel has no EU instance, so setting it in EU would store EU session replays in NA.
    mixpanel_session_replay?: {
        proxy: string;
        lib_base_path: string;
    };
}

export interface BaseConfig {
    home: {
        baseUrl: string;
    };
    archives: {
        url: string;
        api: string;
        login: string;
        logout: string;
    };
    intercom?: {
        app_id?: string;
    };
    microsoft: {
        adminConsentBase: string;
    };
    launch_darkly: {
        client_id: string;
    };
}

const getRegion = (): Region => {
    // The config is sometimes imported in non-browser environments (e.g. during build or in Node.js), so we need to check if `window` is defined before trying to access it.
    // At the moment, specifically formatFilterAsText.test.ts
    if (typeof window !== "undefined") {
        const urlRegion = new URLSearchParams(window.location.search).get(REGION_URL_SEARCH_PARAM);
        const storedRegion = localStorage.getItem(REGION_LOCAL_STORAGE_KEY);

        if (urlRegion && ALL_REGIONS.includes(urlRegion as Region)) {
            return urlRegion as Region;
        }

        if (storedRegion && ALL_REGIONS.includes(storedRegion as Region)) {
            return storedRegion as Region;
        }
    }

    return DEFAULT_REGION;
};

const getStoredRegion = (): Region | null => {
    if (typeof window === "undefined") {
        return null;
    }

    const storedRegion = localStorage.getItem(REGION_LOCAL_STORAGE_KEY);

    return storedRegion && ALL_REGIONS.includes(storedRegion as Region) ? (storedRegion as Region) : null;
};

export const syncRegionFromUrl = (): void => {
    if (typeof window === "undefined") {
        return;
    }

    const urlRegion = new URLSearchParams(window.location.search).get(REGION_URL_SEARCH_PARAM);

    if (urlRegion && ALL_REGIONS.includes(urlRegion as Region)) {
        try {
            localStorage.setItem(REGION_LOCAL_STORAGE_KEY, urlRegion);
        } catch {
            // localStorage unavailable (private browsing, quota exceeded)
        }
    }
};

const setRegion = (region: Region) => {
    localStorage.setItem(REGION_LOCAL_STORAGE_KEY, region);
};

const clearRegion = () => {
    localStorage.removeItem(REGION_LOCAL_STORAGE_KEY);
};

export default {
    get region() {
        return getRegion();
    },
    get storedRegion() {
        return getStoredRegion();
    },
    regions: ALL_REGIONS,
    get: () => {
        if (!config) {
            const region = getRegion();

            config = { ...values[environment]["common"], ...values[environment][region] };
        }

        return config;
    },
    switch: (region: Region) => {
        setRegion(region);
        config = { ...values[environment]["common"], ...values[environment][region] };
    },
    clear: () => {
        clearRegion();
        config = null;
    }
};

const environment = (process.env.APP_ENV as Environment) || "production";

let config: RuntimeConfig | null = null;
